import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import communityService, {
  GameDetailResponse,
  GameLineupPlayerItem,
  GameTimelineEvent,
} from '@/services/communityService';
import { Skeleton } from '@/components/shadcn/skeleton';
import { Card, CardContent } from '@/components/shadcn/card';
import { toast } from 'react-hot-toast';
import { navigateBackToGameRekap } from '@/utils/hostMabarNavigation';

const normalizeTimeline = (raw: unknown): GameTimelineEvent[] => {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (e): e is GameTimelineEvent =>
      e != null && typeof e === 'object' && 'time' in e && 'type' in e && 'team_side' in e
  ) as GameTimelineEvent[];
};

const MabarRekapSelectAssistPage = () => {
  const { gameSlug } = useParams<{ gameSlug: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const scorerId = Number(searchParams.get('scorerId')) || 0;
  const scorerName = searchParams.get('scorerName') || 'Pemain';
  const teamSide = (searchParams.get('team') as 'home' | 'away') || 'home';
  const rawTime = searchParams.get('time') || searchParams.get('timeTag') || "0'";
  const timeTag = rawTime.endsWith("'") ? rawTime : `${rawTime}'`;

  const [game, setGame] = useState<GameDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!gameSlug) return;
    setLoading(true);
    communityService
      .getGameBySlug(gameSlug)
      .then(setGame)
      .catch(() => setGame(null))
      .finally(() => setLoading(false));
  }, [gameSlug]);

  const timeline = useMemo(() => normalizeTimeline(game?.timeline ?? []), [game?.timeline]);

  const lineupPlayers = useMemo(() => {
    if (!game?.lineups) return [];
    return game.lineups.filter((l) => l.team_side === teamSide && l.player_participant_id !== scorerId);
  }, [game?.lineups, teamSide, scorerId]);

  const filteredPlayers = useMemo(() => {
    if (!searchQuery.trim()) return lineupPlayers;
    const q = searchQuery.toLowerCase();
    return lineupPlayers.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.player_number != null && String(p.player_number).includes(q)) ||
        (p.position || p.role || '').toLowerCase().includes(q)
    );
  }, [lineupPlayers, searchQuery]);

  const assistCounts = useMemo(() => {
    const map: Record<number, number> = {};
    timeline.forEach((e) => {
      if (e.type === 'goal' && e.assist_id) {
        map[e.assist_id] = (map[e.assist_id] || 0) + 1;
      }
    });
    return map;
  }, [timeline]);

  const getPositionInfo = (player: GameLineupPlayerItem) => {
    const raw = (player.position || player.role || '').toUpperCase().trim();
    if (raw.includes('GK') || raw.includes('KIPER') || raw.includes('GOAL')) {
      return { code: 'GK', bgClass: 'bg-amber-100 text-amber-700' };
    }
    if (raw.includes('DF') || raw.includes('DEF') || raw.includes('BEK')) {
      return { code: 'DF', bgClass: 'bg-blue-100 text-blue-700' };
    }
    if (raw.includes('FW') || raw.includes('ATT') || raw.includes('ST') || raw.includes('PENYERANG')) {
      return { code: 'FW', bgClass: 'bg-red-100 text-red-700' };
    }
    if (raw.includes('MD') || raw.includes('MID') || raw.includes('GELANDANG')) {
      return { code: 'MD', bgClass: 'bg-emerald-100 text-emerald-700' };
    }
    if (player.player_number === 1) {
      return { code: 'GK', bgClass: 'bg-amber-100 text-amber-700' };
    }
    return { code: 'MD', bgClass: 'bg-emerald-100 text-emerald-700' };
  };

  const handleSaveGoal = async (assistPlayer: GameLineupPlayerItem | null) => {
    if (saving || !gameSlug) return;
    setSaving(true);
    try {
      const assistId = assistPlayer ? assistPlayer.player_participant_id : null;
      const newEvent: GameTimelineEvent = {
        time: timeTag,
        type: 'goal',
        team_side: teamSide,
        player_id: scorerId,
        assist_id: assistId,
      };

      const nextTimeline = [...timeline, newEvent];
      await communityService.updateGameTimeline(gameSlug, nextTimeline);

      const assistText = assistPlayer ? `assist oleh ${assistPlayer.name}` : 'tanpa assist';
      toast.success(`Gol Dicatat: ${scorerName} (menit ${timeTag}) — ${assistText}`);
      // Pop select-assist + select-player so rekap is not stacked above "momen".
      navigateBackToGameRekap(navigate, gameSlug, 2);
    } catch (err: unknown) {
      toast.error('Gagal menyimpan gol');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 max-w-md mx-auto space-y-4 pt-5">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-12 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 pt-5 pb-16 max-w-md mx-auto">
      {/* Desktop Top Header (Hidden on Mobile as MobileTopbar handles topbar) */}
      <div className="hidden md:flex items-center justify-between gap-3 mb-5">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="p-2 -ml-2 rounded-full hover:bg-gray-200 text-gray-700 transition-colors cursor-pointer"
        >
          <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
        </button>

        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-extrabold text-[#0E1726] truncate">Assist</h1>
          <p className="text-xs font-semibold text-gray-400 truncate">
            Opsional · lewati kalau tidak ada
          </p>
        </div>

        <div className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 bg-orange-50 text-[#FC480D]">
          <Icon icon="ph:hand-heart-bold" className="w-5 h-5" />
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative mb-4 md:mt-0">
        <Icon
          icon="ph:magnifying-glass-bold"
          className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2"
        />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Cari nama pemain"
          className="w-full h-11 pl-10 pr-4 rounded-2xl border border-gray-200 bg-white text-xs font-semibold text-gray-800 outline-none focus:border-[#FC480D] transition-colors"
        />
      </div>

      {/* Player List Card */}
      <Card className="rounded-3xl border border-gray-200 shadow-2xs overflow-hidden bg-white mb-4">
        <CardContent className="p-3 sm:p-4 space-y-2">
          {filteredPlayers.map((p) => {
            const playerNum =
              p.player_number != null && p.player_number !== undefined && String(p.player_number).trim() !== ''
                ? p.player_number
                : '#';
            const isBench = p.is_starting === false;
            const posInfo = getPositionInfo(p);
            const aCount = assistCounts[p.player_participant_id] || 0;

            return (
              <div
                key={p.id}
                onClick={() => handleSaveGoal(p)}
                className="p-3 rounded-2xl border border-gray-100 hover:border-gray-200 hover:bg-orange-50/20 flex items-center justify-between gap-3 transition-all cursor-pointer active:scale-98"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {/* Red/Orange Jersey Icon with Number */}
                  <div className="relative w-9 h-10 flex items-center justify-center shrink-0">
                    <Icon icon="ph:t-shirt-fill" className="w-9 h-9 text-[#FC480D]" />
                    <span className="absolute inset-x-0 text-center text-[11px] font-black text-white top-2.5">
                      {playerNum}
                    </span>
                  </div>

                  <div className="min-w-0">
                    <h4 className="text-xs font-bold text-[#0E1726] truncate">{p.name}</h4>
                    <p className="text-[10px] font-semibold text-gray-400">
                      {isBench ? 'Bench' : 'On field'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${posInfo.bgClass}`}>
                    {posInfo.code}
                  </span>
                  {aCount > 0 && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-red-50 text-red-500 border border-red-100">
                      {aCount}x
                    </span>
                  )}
                  <button
                    type="button"
                    className="w-8 h-8 rounded-full bg-orange-50 text-[#FC480D] hover:bg-[#FC480D] hover:text-white flex items-center justify-center transition-colors cursor-pointer"
                  >
                    <Icon icon="ph:plus-bold" className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}

          {filteredPlayers.length === 0 && (
            <div className="text-center py-8 text-xs text-gray-400 font-semibold">
              Pemain tidak ditemukan
            </div>
          )}
        </CardContent>
      </Card>

      {/* Button: Simpan Tanpa Assist (Image 2) */}
      <button
        type="button"
        disabled={saving}
        onClick={() => handleSaveGoal(null)}
        className="w-full py-3 bg-white border border-gray-200 hover:border-gray-300 text-[#0E1726] font-bold text-xs rounded-2xl flex items-center justify-center gap-2 shadow-2xs transition-all cursor-pointer active:scale-98 mb-4"
      >
        <Icon icon="ph:skip-forward-bold" className="w-4 h-4 text-gray-500" />
        <span>Simpan Tanpa Assist</span>
      </button>

      {/* Bottom Info Hint */}
      <div className="flex items-center gap-2 text-xs text-gray-400 font-medium px-2">
        <Icon icon="ph:info-bold" className="w-4 h-4 shrink-0 text-gray-400" />
        <span>Ketuk nama pemain untuk mencatat assist .</span>
      </div>
    </div>
  );
};

export default MabarRekapSelectAssistPage;
